import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Box } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { v4 as uuidv4 } from 'uuid';
import NodeEditDialog from './NodeEditDialog';
import { 
  CustomTree, 
  findNodeById, 
  addChildToNode, 
  removeNodeById, 
  updateNodeById,
  moveSibling
} from './CustomTree';

// Contador global para nós "sem nome"
let newNodeCounter = 1;

// Estrutura base de um nó folha
const baseNodeData = {
  name: "Sem nome",
  label: "",
  description: "",
  type: "string",
  list: false,
  possible_values: [],
  isLeaf: false
};

const TemplateTree = forwardRef((props, ref) => {
  const [treeData, setTreeData] = useState([]);
  const [editNode, setEditNode] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Expor os métodos para serem acessados pelo componente pai
  useImperativeHandle(ref, () => ({
    importJson: importJsonToTree,
    exportJson: exportTreeToJson
  }));

  // Função para abrir o diálogo de edição de um nó
  const handleEditNode = (node) => {
    setEditNode(node);
    setIsEditDialogOpen(true);
  };

  // Função para fechar o diálogo de edição
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditNode(null);
  };

  // Função auxiliar para notificar o componente pai sobre mudanças na árvore
  const notifyDataChange = (data) => {
    if (props.onDataChange) {
      const exportedData = {};
      for (const node of data) {
        const { key, value } = processNodeForExport(node);
        exportedData[key] = value;
      }
      props.onDataChange(exportedData);
    }
  };

  // Função para salvar as mudanças em um nó
  const handleSaveNode = (nodeData) => {
    if (!editNode) return;
    
    const updatedTreeData = updateNodeById(treeData, editNode.id, {
      nodeData: nodeData
    });
    
    setTreeData(updatedTreeData);
    handleCloseEditDialog();
    
    // Notificar sobre a mudança
    notifyDataChange(updatedTreeData);
  };

  // Função para adicionar um novo nó raiz
  const addRootNode = () => {
    const nodeName = `Sem nome ${newNodeCounter}`;
    newNodeCounter++;
    
    const newNode = {
      id: uuidv4(),
      nodeData: { ...baseNodeData, name: nodeName },
      children: []
    };

    const updatedTreeData = [...treeData, newNode];
    setTreeData(updatedTreeData);
    
    // Notificar o componente pai sobre a mudança
    notifyDataChange(updatedTreeData);
  };

  // Função para adicionar um nó filho
  const addChildNode = (parentId) => {
    const parentNode = findNodeById(treeData, parentId);
    
    if (!parentNode) return;

    // Nós folha não podem ter filhos
    if (parentNode.nodeData.isLeaf) {
      alert("Nós folha não podem ter filhos. Altere o tipo do nó para 'agrupador' primeiro.");
      return;
    }

    const nodeName = `Sem nome ${newNodeCounter}`;
    newNodeCounter++;
    
    const newNode = {
      id: uuidv4(),
      nodeData: { ...baseNodeData, name: nodeName },
      children: []
    };

    const updatedTreeData = addChildToNode(treeData, parentId, newNode);
    setTreeData(updatedTreeData);
    
    // Notificar o componente pai sobre a mudança
    notifyDataChange(updatedTreeData);
  };

  // Função para remover um nó
  const removeNode = (nodeId) => {
    if (window.confirm("Tem certeza que deseja remover este item e todos os seus filhos?")) {
      const updatedTreeData = removeNodeById(treeData, nodeId);
      setTreeData(updatedTreeData);
      
      // Notificar o componente pai sobre a mudança
      notifyDataChange(updatedTreeData);
    }
  };

  // Função para lidar com drag and drop
  const handleDragDrop = (dragNodeId, dropTargetId, position) => {
    const dragNode = findNodeById(treeData, dragNodeId);
    
    if (!dragNode) return;
    
    const dropTarget = findNodeById(treeData, dropTargetId);
    if (!dropTarget) return;
    
    // Com base na posição, decidimos o que fazer
    let updatedTreeData;
    
    if (position === 'inside') {
      // Se o target for um nó folha, não permitimos soltar nele
      if (dropTarget.nodeData.isLeaf) {
        alert("Não é possível adicionar itens a um nó folha.");
        return;
      }
      
      // Remover o nó da sua posição atual
      let newTreeData = removeNodeById(treeData, dragNodeId);
      
      // Adicionar o nó à nova posição como filho
      updatedTreeData = addChildToNode(newTreeData, dropTargetId, dragNode);
    } else {
      // Mover como irmão (antes ou depois)
      updatedTreeData = moveSibling(treeData, dragNodeId, dropTargetId, position);
    }
    
    setTreeData(updatedTreeData);
    
    // Notificar o componente pai sobre a mudança
    notifyDataChange(updatedTreeData);
  };

  // Função para processar um nó durante a importação
  const processNodeForImport = (key, value) => {
    // Verificar se o valor é um array (lista)
    const isList = Array.isArray(value);
    // Extrair o valor real (se for lista, pegar o primeiro item)
    const realValue = isList ? value[0] : value;
    
    // Verificar se é um nó folha (possui type, label ou description)
    const isLeafNode = realValue && (
      typeof realValue === 'object' && 
      (realValue.type || realValue.label || realValue.description)
    );
    
    if (isLeafNode) {
      // Determinar o tipo real baseado nos dados
      let nodeType = realValue.type || "string";
      
      // Se o tipo é "string" (ou "text" por compatibilidade) e tem possible_values,
      // então deve ser convertido para o tipo "possible_values"
      if ((nodeType === "text" || nodeType === "string") && 
          realValue.possible_values && 
          Array.isArray(realValue.possible_values) && 
          realValue.possible_values.length > 0) {
        nodeType = "possible_values";
        console.log(`Convertendo nó "${key}" de tipo "${realValue.type}" para "possible_values" com valores:`, realValue.possible_values);
      }
      
      return {
        id: uuidv4(),
        nodeData: {
          name: key,
          label: realValue.label || key,
          description: realValue.description || "",
          type: nodeType,
          list: isList, // Definir list com base no fato de ser array
          possible_values: realValue.possible_values || [],
          isLeaf: true
        },
        children: []
      };
    }
    
    // Caso contrário, é um nó interno com filhos
    const children = [];
    
    // Processar os filhos se o valor for um objeto
    if (typeof realValue === 'object' && realValue !== null) {
      for (const [childKey, childValue] of Object.entries(realValue)) {
        // Ignorar propriedades que são parte do nó folha
        if (childKey !== 'type' && childKey !== 'label' && childKey !== 'description' && 
            childKey !== 'possible_values') {
          children.push(processNodeForImport(childKey, childValue));
        }
      }
    }
    
    return {
      id: uuidv4(),
      nodeData: {
        name: key,
        label: "",
        description: "",
        type: "string",
        list: isList, // Definir list com base no fato de ser array
        possible_values: [],
        isLeaf: false
      },
      children: children
    };
  };

  // Função para importar JSON para a árvore
  const importJsonToTree = (jsonData) => {
    try {
      // Limpar os dados existentes antes de importar
      console.log('Importando novo template, limpando dados existentes...');
      
      const newTreeData = [];
      
      for (const [key, value] of Object.entries(jsonData)) {
        newTreeData.push(processNodeForImport(key, value));
      }
      
      // Substituir completamente os dados existentes pelos novos
      setTreeData(newTreeData);
      
      // Notificar o componente pai (se houver onDataChange)
      notifyDataChange(newTreeData);
    } catch (error) {
      console.error('Erro ao processar JSON:', error);
      alert('Ocorreu um erro ao processar o JSON. Verifique o formato do arquivo.');
    }
  };

  // Função para extrair os dados de um nó durante a exportação
  const processNodeForExport = (node) => {
    const nodeData = node.nodeData;
    
    // Se for um nó folha ou não tiver filhos
    if (nodeData.isLeaf || node.children.length === 0) {
      const result = {};
      
      // Converter o tipo para o formato esperado na exportação
      if (nodeData.type) {
        // Se o tipo é "possible_values", exportar como "string"
        if (nodeData.type === "possible_values") {
          result.type = "string";
          console.log(`Exportando nó "${nodeData.name}" de tipo "possible_values" como "string" com valores:`, nodeData.possible_values);
        } else {
          result.type = nodeData.type;
        }
      }
      
      if (nodeData.label) result.label = nodeData.label;
      if (nodeData.description) result.description = nodeData.description;
      // Não adicionar mais o atributo list ao resultado
      if (nodeData.possible_values && nodeData.possible_values.length > 0) {
        result.possible_values = nodeData.possible_values;
      }
      
      // Se for uma lista, envolve o resultado em colchetes
      if (nodeData.list) {
        return { key: nodeData.name, value: [result] };
      } else {
        return { key: nodeData.name, value: result };
      }
    }
    
    // Se for um nó interno com filhos
    const result = {};
    
    for (const child of node.children) {
      const { key, value } = processNodeForExport(child);
      result[key] = value;
    }
    
    // Se o nó é lista, envolva o resultado em colchetes
    if (nodeData.list) {
      return { key: nodeData.name, value: [result] };
    } else {
      return { key: nodeData.name, value: result };
    }
  };

  // Função para exportar a árvore para JSON
  const exportTreeToJson = () => {
    try {
      const result = {};
      
      for (const node of treeData) {
        const { key, value } = processNodeForExport(node);
        result[key] = value;
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao exportar para JSON:', error);
      alert('Ocorreu um erro ao exportar para JSON.');
      return {};
    }
  };

  return (
    <div>
      <Box mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addRootNode}
          style={{ backgroundColor: 'white', color: '#3f51b5' }}
        >
          Adicionar Item
        </Button>
      </Box>
      
      <div style={{ height: 600, width: '100%' }}>
        <CustomTree
          treeData={treeData}
          onNodeAdd={addChildNode}
          onNodeRemove={removeNode}
          onNodeEditOpen={handleEditNode}
          onDragDrop={handleDragDrop}
          emptyMessage="Nenhum item criado ainda."
        />
      </div>
      
      {isEditDialogOpen && editNode && (
        <NodeEditDialog
          open={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          onSave={handleSaveNode}
          nodeData={editNode.nodeData}
        />
      )}
    </div>
  );
});

export default TemplateTree; 